import { CheckCircle2, Mail, MessageSquare, SkipForward, XCircle } from "lucide-react";

/**
 * Renders a single channel's stat module (SMS or Email).
 *
 * Used by:
 *  - Campaign cards (compact variant)
 *  - Campaign detail Channel Performance section (full variant)
 *
 * Honest labels — never use "Delivered" until provider webhooks land. Twilio 201
 * is "Accepted by Twilio"; SendGrid 202 is "Accepted by SendGrid".
 */

const ACCENTS = {
    sms: {
        label: "SMS",
        Icon: MessageSquare,
        acceptedLabel: "Accepted by Twilio",
        ring: "border-emerald-200/70",
        bar: "bg-emerald-500",
        chipBg: "bg-emerald-50",
        chipText: "text-emerald-700",
        iconBg: "bg-emerald-50 text-emerald-600",
    },
    email: {
        label: "Email",
        Icon: Mail,
        acceptedLabel: "Accepted by SendGrid",
        ring: "border-blue-200/70",
        bar: "bg-blue-500",
        chipBg: "bg-blue-50",
        chipText: "text-blue-700",
        iconBg: "bg-blue-50 text-blue-600",
    },
};

function fmt(n) {
    return typeof n === "number" ? n.toLocaleString() : "0";
}

function tinyTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString([], {
            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return "—";
    }
}

/**
 * Compact mini-module used inside a campaign card.
 * Shows: header (icon + label), accepted/failed/skipped chips, progress bar.
 */
export function ChannelStatsMini({ channel, stats, testid }) {
    const a = ACCENTS[channel];
    if (!a) return null;
    const accepted = stats?.accepted ?? 0;
    const failed = stats?.failed ?? 0;
    const skipped = stats?.skipped ?? 0;
    const processed = stats?.processed ?? 0;
    const target = stats?.target ?? 0;
    const pct = target ? Math.min(100, Math.round((processed / target) * 100)) : 0;
    return (
        <div className={`rounded-lg border ${a.ring} bg-white/60 p-3`} data-testid={testid || `channel-mini-${channel}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                    <span className={`h-5 w-5 rounded-md flex items-center justify-center ${a.iconBg}`}>
                        <a.Icon className="h-3 w-3" strokeWidth={2} />
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">{a.label}</span>
                </div>
                <span className="text-[10px] text-muted-foreground tabular">
                    {fmt(processed)}{target ? <> / {fmt(target)}</> : null}
                </span>
            </div>
            <div className="grid grid-cols-3 gap-1.5 text-[11px] mb-2">
                <Cell label="Accepted" value={accepted} tone="emerald" />
                <Cell label="Failed" value={failed} tone={failed ? "rose" : "gray"} />
                <Cell label="Skipped" value={skipped} tone={skipped ? "amber" : "gray"} />
            </div>
            <div className="h-1 rounded-full bg-gray-100 overflow-hidden" data-testid={`channel-mini-bar-${channel}`}>
                <div className={`h-full ${a.bar}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

/**
 * Full-size channel module used on the Campaign Detail page.
 * Shows: header, accepted/failed/skipped large numbers, progress bar with %,
 * last activity timestamp.
 */
export function ChannelStatsModule({ channel, stats, testid }) {
    const a = ACCENTS[channel];
    if (!a) return null;
    const accepted = stats?.accepted ?? 0;
    const failed = stats?.failed ?? 0;
    const skipped = stats?.skipped ?? 0;
    const processed = stats?.processed ?? 0;
    const target = stats?.target ?? 0;
    const pct = target ? Math.min(100, Math.round((processed / target) * 100)) : 0;
    const last = stats?.last_activity_at;
    return (
        <div
            className={`rounded-xl border ${a.ring} bg-white p-5 shadow-sm`}
            data-testid={testid || `channel-module-${channel}`}
        >
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                    <span className={`h-8 w-8 rounded-md flex items-center justify-center ${a.iconBg}`}>
                        <a.Icon className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <div>
                        <div className="text-sm font-semibold text-gray-900">{a.label} performance</div>
                        <div className="text-[11px] text-muted-foreground">app-owned stats</div>
                    </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider rounded-full ${a.chipBg} ${a.chipText} px-2 py-0.5 font-medium`}>
                    {channel === "sms" ? "Twilio" : "SendGrid"}
                </span>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
                <BigStat
                    label={a.acceptedLabel}
                    value={accepted}
                    Icon={CheckCircle2}
                    tone="emerald"
                    testid={`channel-${channel}-accepted`}
                />
                <BigStat
                    label="Failed"
                    value={failed}
                    Icon={XCircle}
                    tone={failed ? "rose" : "gray"}
                    testid={`channel-${channel}-failed`}
                />
                <BigStat
                    label="Skipped"
                    value={skipped}
                    Icon={SkipForward}
                    tone={skipped ? "amber" : "gray"}
                    testid={`channel-${channel}-skipped`}
                />
            </div>

            <div className="space-y-1.5" data-testid={`channel-${channel}-progress-block`}>
                <div className="flex items-baseline justify-between text-xs">
                    <div>
                        <span className="font-display font-semibold text-gray-900 tabular text-sm">
                            {fmt(processed)}
                        </span>
                        {target ? <span className="text-muted-foreground"> / {fmt(target)}</span> : null}
                        <span className="text-muted-foreground ml-1.5">processed</span>
                    </div>
                    <span className="text-muted-foreground tabular">{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                        className={`h-full ${a.bar}`}
                        style={{ width: `${pct}%` }}
                        data-testid={`channel-${channel}-bar`}
                    />
                </div>
            </div>

            <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>Last activity</span>
                <span className="tabular" data-testid={`channel-${channel}-last-activity`}>{tinyTime(last)}</span>
            </div>
        </div>
    );
}

function Cell({ label, value, tone }) {
    const toneCls = {
        emerald: "text-emerald-700",
        rose: "text-rose-700",
        amber: "text-amber-700",
        gray: "text-gray-600",
    }[tone] || "text-gray-700";
    return (
        <div className="rounded-md bg-gray-50/70 border border-border px-2 py-1">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`font-medium tabular ${toneCls}`}>{fmt(value)}</div>
        </div>
    );
}

function BigStat({ label, value, Icon, tone, testid }) {
    const toneCls = {
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200/70",
        rose: "bg-rose-50 text-rose-700 border-rose-200/70",
        amber: "bg-amber-50 text-amber-700 border-amber-200/70",
        gray: "bg-gray-50 text-gray-700 border-border",
    }[tone] || "bg-gray-50 text-gray-700 border-border";
    return (
        <div className={`rounded-lg border ${toneCls} px-3 py-2.5`} data-testid={testid}>
            <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-wider opacity-80 leading-tight">{label}</div>
                <Icon className="h-3.5 w-3.5 opacity-70 shrink-0" strokeWidth={2} />
            </div>
            <div className="font-display text-xl font-semibold tabular mt-0.5">{fmt(value)}</div>
        </div>
    );
}
